import type { RootState } from '../../../store';
import type { NormalizedItem as CachedItem } from '@/components/itemList/types';

// Re-export the canonical NormalizedItem so shake components keep compiling
export type NormalizedItem = CachedItem;

const EMPTY: NormalizedItem[] = [];

/**
 * Read items from the global itemsSlice cache.
 *
 * Prefers an explicit projectId (the shake feature knows its own project via
 * ShakeProvider) and falls back to state.project.projectId, which is only set
 * while ProjectPage is mounted — the shake modal also opens on other views.
 */
export const selectAllProjectItems = (
  state: RootState,
  projectId?: string | null,
): NormalizedItem[] => {
  const pid = projectId ?? (state.project.projectId as string | null);

  if (pid) {
    const cached = state.items.byParent[pid];
    if (cached?.items) return cached.items;
  }

  return EMPTY;
};

// Filter helpers — pass the shake projectId so lookups work outside ProjectPage
export const selectImages = (state: RootState, projectId?: string | null): NormalizedItem[] =>
  selectAllProjectItems(state, projectId).filter((it) => it.type === 'image');

export const selectAudios = (state: RootState, projectId?: string | null): NormalizedItem[] =>
  selectAllProjectItems(state, projectId).filter((it) => it.type === 'audio');

export const selectVideos = (state: RootState, projectId?: string | null): NormalizedItem[] =>
  selectAllProjectItems(state, projectId).filter((it) => it.type === 'video');

// Resolve one by id, returning normalized or null
export const selectItemById = (state: RootState, id: string): NormalizedItem | null =>
  selectAllProjectItems(state).find((it) => it.id === id) || null;

export const selectItemByNameAndType = (state: RootState, name: string, type: string): NormalizedItem | null =>
  selectAllProjectItems(state).find((it) => it.name === name && it.type === type) || null;
